import { useState } from "react";
import { DocFieldConfig } from "../../models/config/DocFieldConfig";
import { TransformRuleFactory } from "../../models/transformation/factory/TransformRuleFactory";
import { TransformRule } from "../../models/transformation/TransformRule";
import "../../styles/transformation/transformationEditorList.css";
import { TransformRuleType } from "../../types/transformation/TransformationRuleTypes";
import { Button } from "../buttons/Button";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import { TTTransformAddRule } from "../tooltips/ConfigurationTooltips";
import { ValidateTransformTitle } from "../validation/ValidationEditorList";
import { Footer } from "./Footer";
import { TransformationEditorHeader } from "./TransformationEditorHeader";
import { TransformRuleEditor } from "./TransformRuleEditor";

export { TransformationEditorList };

type Props = {
  /** Title of main rules. */
  mainListTitle: string;
  testValue?: string;
  fields: DocFieldConfig[];
  variables: string[];
  nonEditableVariables: string[];
  // header?: (addRule: (type: TransformRuleType) => void) => React.ReactNode;
  // footer?: (rules: TransformRule[], variables: string[]) => React.ReactNode;
  fieldName: string;
  rules: TransformRule[];
  /**
   * If undefined, effective rules not shown
   */
  effectiveRules?: TransformRule[];
  save: (rules: TransformRule[]) => void;
  // trigger Openness.
  setOpen: (open: boolean) => void;
  // open component
  open: boolean;
};

/**
 * component responsible for adding rule
 * responsible for merging/updating rules
 * TODO: break this component up ! the testing functionality should be isolated from the viewing functionality
 * TOOD: move everything in .header to an outside component so DLFEditorList can embed these proper
 */
const TransformationEditorList = (props: Props) => {
  /**  */
  const [addNewRule, setAddNewRule] = useState<TransformRuleType | undefined>(
    undefined,
  );
  const [showEffective, setShowEffective] = useState(
    (props.effectiveRules ?? []).length !== 0,
  );
  const [showActual, setShowActual] = useState(true);

  function mergeRules(
    rules: TransformRule[],
    newRule: TransformRule | null,
    oldRuleId: string,
  ): TransformRule[] {
    if (newRule == null) {
      return rules.filter((x) => x.getRuleId() !== oldRuleId);
    }

    // try to find rule with oldRuleId
    const finalRules = [...rules];
    const ruleIndex = finalRules.findIndex((x) => x.getRuleId() === oldRuleId);
    if (ruleIndex == undefined) {
      console.warn("RuleIndex could not be found for modified TransformRule");
      return rules;
    }
    finalRules.splice(ruleIndex, 1, newRule);

    return finalRules;
  }

  const addBlankRule = (type: TransformRuleType) => {
    // add rule
    setAddNewRule(type);
  };

  const effectiveRules = props.effectiveRules ?? [];

  return (
    <div className={"transformationEditorList"}>
      {/* {props.header && props.header(addBlankRule)} */}
      <TransformationEditorHeader
        fieldName={props.fieldName}
        setOpen={props.setOpen}
        open={props.open}
        addBlankRule={addBlankRule}
      ></TransformationEditorHeader>
      <ValidateTransformTitle
        title={props.mainListTitle}
        count={props.rules.length}
        collapse={showActual}
        setCollapse={setShowActual}
      >
        {/* Add Rule Button */}
        <AdvancedTooltip short overflow tooltipText={TTTransformAddRule}>
          {(tProps) => (
            <Button
              {...tProps}
              name={"addrule"}
              onClick={(e) => {
                addBlankRule("T_REPALL");
                props.setOpen(true);
                e.stopPropagation();
              }}
            >
              Add Rule
            </Button>
          )}
        </AdvancedTooltip>
      </ValidateTransformTitle>

      <div className={`rules ${showActual ? "open" : "closed"}`}>
        {/* closed Editor */}
        {props.rules
          .sort((a, b) => a.position - b.position)
          .map((rule) => {
            return (
              showActual && (
                <TransformRuleEditor
                  key={rule.getRuleId()}
                  display={props.open ? "edit" : "view"}
                  fields={props.fields}
                  rule={rule}
                  variables={props.variables}
                  changedRuleType={(newRule, oldRuleId) => {
                    const mergedRules = mergeRules(
                      props.rules,
                      newRule,
                      oldRuleId,
                    );
                    mergedRules.forEach((x, i) => {
                      x.position = i;
                    });
                    // setRules(mergedRules);
                    props.save(mergedRules);
                  }}
                  update={(newRule, oldRuleId) => {
                    const mergedRules = mergeRules(
                      props.rules,
                      newRule,
                      oldRuleId,
                    );
                    mergedRules.forEach((x, i) => {
                      x.position = i;
                    });
                    props.save(mergedRules);
                  }}
                />
              )
            );
          })}
        {addNewRule && (
          <TransformRuleEditor
            display={props.open ? "edit" : "view"}
            variables={props.variables}
            fields={props.fields}
            rule={TransformRuleFactory.createRule({
              discriminator: addNewRule,
              fieldName: props.fieldName,
              position: props.rules.length,
            })}
            changedRuleType={(newRule, oldRuleId) => {
              // if (newRule && !newRule.isConfigured()) {
              //     return undefined;
              // }
              let mergedRules;
              if (!newRule) {
                mergedRules = props.rules;
              } else {
                mergedRules = props.rules.concat([newRule]);
              }
              mergedRules.forEach((x, i) => {
                x.position = i;
              });
              props.save(mergedRules);
              setAddNewRule(undefined);
            }}
            update={(newRule, oldRuleId) => {
              let mergedRules;
              if (!newRule) {
                mergedRules = props.rules;
              } else {
                mergedRules = props.rules.concat([newRule]);
              }
              mergedRules.forEach((x, i) => {
                x.position = i;
              });
              props.save(mergedRules);
              setAddNewRule(undefined);
              props.setOpen(true);
            }}
          />
        )}
      </div>

      <div
        className="effective"
        style={{
          display: props.effectiveRules == undefined ? "none" : "inherit",
        }}
      >
        {/* Hide collapse icon if no doc type rules exist */}
        <ValidateTransformTitle
          collapse={effectiveRules!.length === 0 ? undefined : showEffective}
          count={effectiveRules!.length}
          setCollapse={setShowEffective}
          title={"Document Type Transform Rules"}
        />

        <div className={"rules"}>
          {effectiveRules!.map((rule, i) => {
            return (
              showEffective && (
                <TransformRuleEditor
                  key={i + "" + showEffective}
                  display="view"
                  rule={rule}
                  variables={[]}
                  update={() => ""}
                  fields={[]}
                />
              )
            );
          })}
        </div>
      </div>
      <Footer
        rules={props.rules}
        testValue={props.testValue}
        mainListTitle={props.mainListTitle}
        effectiveRules={props.effectiveRules}
        docFields={props.fields.map((x) => x.fieldName)}
        variables={props.variables}
      />
    </div>
  );
};
